import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import type { ScenarioGraphNodeType } from 'types/api/scenarios/scenario'
import type { EvaluationConfiguration } from 'types/api/status-checks/evaluation'
import type { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

enum IntegrationSpecificKeys {
  AWS_ACCESS_KEY_ID = 'AWS_ACCESS_KEY_ID',
  AWS_SECRET_ACCESS_KEY = 'AWS_SECRET_ACCESS_KEY',
  AWS_IAM_ROLE_ARN = 'AWS_IAM_ROLE_ARN'
}

export enum ObservabilityToolType {
  DATADOG = 'DATADOG',
  PAGERDUTY = 'PAGERDUTY',
  NEWRELIC = 'NEWRELIC',
  GRAFANA = 'GRAFANA',
  DYNATRACE = 'DYNATRACE',
  APPDYNAMICS = 'APPDYNAMICS',
  AWS = 'AWS',
  CUSTOM = 'CUSTOM'
}

export enum PredicateType {
  String = 'String',
  Number = 'Number',
  Boolean = 'Boolean'
}

// EN-8466 to confirm: these are the strings the api expects
export enum StatusCheckCategory {
  ERRORS = 'ERRORS',
  LATENCY = 'LATENCY',
  REQUESTS = 'REQUESTS',
  UNKNOWN = 'UNKNOWN'
}

export enum TeamExternalIntegrationAuthenticationStatus {
  Authenticated = 'AUTHENTICATED',
  UnAuthenticated = 'UNAUTHENTICATED',
  Untested = 'UNTESTED'
}

/**
 * This is also used on the team integrations page, so the naming convention will be left as is,
 * since its usage there is not Health Check specific.
 */
export interface StatusCheckHeader {
  key: string
  value: string
  label?: string
}

export type EndpointConfiguration = {
  url?: string
} & (
  | { method?: never; payload?: never; headers?: never }
  | { method: 'GET'; payload?: never; headers?: never }
  | {
      method: 'POST'
      payload: string
      headers: { 'content-type': string; [key: string]: string }
    }
)

export interface EndpointResponse {
  httpStatusCode?: number
  requestLatencyMs?: number
  contentType?: string
  responseBody?: string
}

// Shared by both 'StatusCheck' and 'ServiceStatusCheck'
interface StatusCheckBase {
  category: StatusCheckCategory
  description?: string
  isContinuous: boolean
  name: string
}

export interface StatusCheck extends StatusCheckBase {
  category: StatusCheckCategory
  createdAt: string
  createdBy: string
  description?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfiguration
  identifier: string
  isContinuous: boolean
  isPrivateNetwork: boolean
  name: string
  pollingIntervalSeconds?: number
  rawEndpointConfiguration?: EndpointConfiguration
  teamExternalIntegration: StatusCheckTeamExternalIntegration
  teamId: string
  thirdPartyPresets: string
  type?: ScenarioGraphNodeType
}

export interface StatusCheckTeamExternalIntegration {
  name: string
  type?: IntegrationType
  domain?: string
  observabilityToolType?: ObservabilityToolType
  integrationSpecificValues?: IntegrationSpecificValues
}

// can omit sending if it's null
export type EvaluationConfigurationCreateRequest = Omit<
  EvaluationConfiguration,
  'okLatencyMaxMs'
> & {
  okLatencyMaxMs?: number
}

export interface StatusCheckCreateRequest {
  category: StatusCheckCategory
  description?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfigurationCreateRequest
  isContinuous?: boolean
  isPrivateNetwork?: boolean
  name: string
  pollingIntervalSeconds: number
  rawEndpointConfiguration: Omit<EndpointConfiguration, 'method'>
  teamExternalIntegration?: StatusCheckTeamExternalIntegration
  thirdPartyPresets?: string
  webhookId?: string
}

export interface StatusCheckEvaluationPredicate {
  type?: PredicateType
  jqQuery?: string
  jpQuery?: string
  comparator?: string
  rValue?: any
}

export interface ResponseBodyEvaluation {
  op: 'AND' | 'OR'
  predicates: StatusCheckEvaluationPredicate[]
}

export interface ServiceStatusCheck extends StatusCheckBase {
  evaluationConfiguration: EvaluationConfiguration
  id?: string // why is this optional?
  integration: TeamExternalIntegration
  isAutogenerated?: boolean
}

/*
 * When we transform a `StatusCheck` to a `ServiceStatusCheck`, we now need to include additional
 * fields from the StatusCheck. These fields are needed to populate fields on the Health Check
 * Create/Edit forms. We don't wish to add these to `ServiceStatusCheck`, as this doesn't align
 * with the data model of the response from `services/:serviceId/status-checks`.
 */
export type ServiceStatusCheckFromStatusCheck = Omit<
  ServiceStatusCheck,
  'evaluationConfiguration'
> &
  Pick<
    StatusCheckFormValues,
    | 'evaluationConfiguration'
    | 'endpointConfiguration'
    | 'pollingIntervalSeconds'
  >

export type IntegrationSpecificValues = {
  [key in IntegrationSpecificKeys]?: string
}

export interface TeamExternalIntegration {
  type: IntegrationType
  name: string
  id?: string
  url?: string
  headers?: StatusCheckHeader[]
  isPrivateNetworkIntegration?: boolean
  isConfigured?: boolean
  authUrl?: string
  lastAuthenticationStatus?: TeamExternalIntegrationAuthenticationStatus
  canBeUsedAsStatusCheck?: boolean
  createdAt: Date | string
  sortIndex?: number
  isOther?: boolean
  observabilityToolType?: ObservabilityToolType
  domain?: string | null
  integrationSpecificValues?: IntegrationSpecificValues
}

export interface TeamExternalIntegrationResponse {
  userId: string
  type: IntegrationType
  observabilityToolType: ObservabilityToolType
  createdAt: Date | string
  domain: string | null
  url: string
  headers: StatusCheckHeader[]
  name: string
  teamId: string
  privateNetwork: boolean
  canBeUsedAsStatusCheck: boolean
  lastAuthenticationStatus: TeamExternalIntegrationAuthenticationStatus
  integrationSpecificValues?: IntegrationSpecificValues
}
